import React from 'react';

/** Portrait card glyph. */
export const CardFace = ({ fill = '#f0c400', size = 14 }) => (
  <svg
    width={size * 0.75}
    height={size}
    viewBox="0 0 12 16"
    aria-hidden
    focusable="false"
  >
    <rect
      x="0.75"
      y="0.75"
      width="10.5"
      height="14.5"
      rx="1.6"
      fill={fill}
      stroke="rgba(0,0,0,0.22)"
      strokeWidth="0.8"
    />
  </svg>
);

export const YellowCard = ({ size = 14 }) => (
  <CardFace fill="#f0c400" size={size} />
);

export const RedCard = ({ size = 14 }) => (
  <CardFace fill="#d32f2f" size={size} />
);

/** Two season yellows — overlapping yellow cards (one SVG). */
export const TwoYellowCards = ({ size = 14 }) => (
  <svg
    width={size * 0.95}
    height={size}
    viewBox="0 0 16 16"
    aria-hidden
    focusable="false"
  >
    <rect
      x="0.6"
      y="1.4"
      width="9.2"
      height="12.8"
      rx="1.4"
      fill="#f0c400"
      stroke="rgba(0,0,0,0.22)"
      strokeWidth="0.7"
    />
    <rect
      x="5.2"
      y="0.6"
      width="9.2"
      height="12.8"
      rx="1.4"
      fill="#f0c400"
      stroke="rgba(0,0,0,0.22)"
      strokeWidth="0.7"
    />
  </svg>
);

/** Second yellow send-off: yellow behind, red in front (one SVG). */
export const SecondYellowCards = ({ size = 14 }) => (
  <svg
    width={size * 0.95}
    height={size}
    viewBox="0 0 16 16"
    aria-hidden
    focusable="false"
  >
    <rect
      x="0.6"
      y="1.4"
      width="9.2"
      height="12.8"
      rx="1.4"
      fill="#f0c400"
      stroke="rgba(0,0,0,0.22)"
      strokeWidth="0.7"
    />
    <rect
      x="5.4"
      y="0.6"
      width="9.2"
      height="12.8"
      rx="1.4"
      fill="#d32f2f"
      stroke="rgba(0,0,0,0.22)"
      strokeWidth="0.7"
    />
  </svg>
);

/** Full injured status disc (no CSS circle). */
export const InjuredDisc = ({ size = 20 }) => {
  const px = Math.max(14, Math.round(size / 2) * 2);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 20 20"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="10" r="10" fill="#de3e32" />
      <path
        d="M6 10h8M10 6v8"
        fill="none"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
};

/**
 * Captain disc — path glyph, not font text, so it stays centred at any DPR.
 * auto → C with a small asterisk.
 */
export const CaptainDiscSvg = ({ size = 20, auto = false }) => {
  const px = Math.max(14, Math.round(size / 2) * 2);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 20 20"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="10" r="10" fill="#111" />
      {/* Smaller open arc = C with breathing room in the disc */}
      <path
        d="M12.75 7.05a3.95 3.95 0 1 0 0 5.9"
        fill="none"
        stroke="#fff"
        strokeWidth="2.05"
        strokeLinecap="round"
      />
      {auto && (
        <g
          fill="none"
          stroke="#fff"
          strokeWidth="1.05"
          strokeLinecap="round"
        >
          <path d="M15.35 3.55v2.4M14.15 4.75h2.4" />
          <path d="M14.3 3.7l2.1 2.1M16.4 3.7l-2.1 2.1" />
        </g>
      )}
    </svg>
  );
};

/**
 * Coloured status disc with a nested 24×24 stroke glyph.
 * Same Lucide paths as assets/img/header/*.svg — baked into one SVG so the
 * icon stays centred on 1× / high-DPI (no CSS circle + nested icon).
 */
export const StatusDiscSvg = ({ size = 20, bg, children }) => {
  const px = Math.max(14, Math.round(size / 2) * 2);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 20 20"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="10" r="10" fill={bg} />
      <svg
        x="3.5"
        y="3.5"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </svg>
    </svg>
  );
};

export const GlyphThumbsUp = () => (
  <>
    <path d="M7 10v12" />
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
  </>
);

export const GlyphThumbsDown = () => (
  <>
    <path d="M17 14V2" />
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
  </>
);

export const GlyphLoanIn = () => (
  <>
    <path d="m10 17 5-5-5-5" />
    <path d="M15 12H3" />
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
  </>
);

export const GlyphLoanOut = () => (
  <>
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
  </>
);

export const GlyphDollar = () => (
  <>
    <line x1="12" x2="12" y1="2" y2="22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </>
);

export const GlyphOffer = () => (
  <>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </>
);

export const GlyphAcademy = () => (
  <>
    <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" />
    <path d="M22 10v6" />
    <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
  </>
);

/** Ball mark for goal events. */
export const GoalBall = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden
    focusable="false"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="#f5f5f5"
      stroke="#222"
      strokeWidth="1.4"
    />
    <path
      d="M12 2v4M12 18v4M2 12h4M18 12h4M5.5 5.5l2.8 2.8M15.7 15.7l2.8 2.8M18.5 5.5l-2.8 2.8M8.3 15.7l-2.8 2.8"
      stroke="#222"
      strokeWidth="1.2"
      fill="none"
    />
    <circle
      cx="12"
      cy="12"
      r="3.2"
      fill="none"
      stroke="#222"
      strokeWidth="1.2"
    />
  </svg>
);

/** Classic football sub mark: green on, red off. */
export const SubstitutionMark = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    aria-hidden
    focusable="false"
  >
    <path d="M3.5 1.5 7 5.5 0 5.5Z" fill="#2f9e44" />
    <path d="M10.5 12.5 7 8.5 14 8.5Z" fill="#e03131" />
  </svg>
);
